/**
 * PortfolioSizeChart Component
 * Simple line chart tracking portfolio size over time
 */

import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useBreakpointValue } from "@chakra-ui/react";

interface PortfolioSizeChartProps {
  portfolioSize: number;
  portfolioChangePercent: number | null;
  startingBalance: number;
  useWhiteColor?: boolean;
}

const PortfolioSizeChart: React.FC<PortfolioSizeChartProps> = ({
  portfolioSize,
  portfolioChangePercent,
  startingBalance,
  useWhiteColor = false,
}) => {
  // Generate mock time series data for past 30 days
  // Uses full vertical range (0-100) for maximum height
  const chartData = useMemo(() => {
    // Create a trend that goes up if positive, down if negative
    // Data uses full 0-100 range to maximize vertical space
    // If percentage is null, check if portfolio size is greater than starting balance
    const isPositive = portfolioChangePercent !== null 
      ? portfolioChangePercent >= 0 
      : portfolioSize >= startingBalance;
    const baseTrend = isPositive 
      ? [5, 20, 12, 35, 25, 55, 45, 90]  // Upward trend using full vertical range
      : [90, 75, 80, 60, 65, 40, 50, 10]; // Downward trend using full vertical range
    
    return baseTrend;
  }, [portfolioChangePercent, portfolioSize, startingBalance]);

  // Use white if useWhiteColor is true, otherwise use green if positive, red if negative
  const isPositive = portfolioChangePercent !== null 
    ? portfolioChangePercent >= 0 
    : portfolioSize >= startingBalance;
  const color = useWhiteColor ? "#FFFFFF" : (isPositive ? "#17FEAE" : "#EF4444");

  const chartOptions: ApexOptions = useMemo(() => ({
    chart: {
      type: "area",
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
      },
      padding: {
        top: 0,
        right: -10,
        bottom: 0,
        left: -10,
      },
      zoom: {
        enabled: false,
      },
      selection: {
        enabled: false,
      },
      dropShadow: {
        enabled: true,
        color: color,
        top: 0,
        left: 0,
        blur: 4,
        opacity: 0.5,
      },
    },
    stroke: {
      curve: "smooth",
      width: 3,
      colors: [color],
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.5,
        gradientToColors: [color],
        inverseColors: false,
        opacityFrom: 0,
        opacityTo: 0,
        stops: [0, 100],
      },
      colors: [color],
    },
    colors: [color],
    dataLabels: {
      enabled: false,
    },
    grid: {
      show: false,
      padding: {
        top: -15,
        right: -15,
        bottom: -15,
        left: -15,
      },
    },
    xaxis: {
      labels: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      crosshairs: {
        show: false,
      },
    },
    yaxis: {
      labels: {
        show: false,
      },
      min: 0,
      max: 100,
      forceNiceScale: false,
      crosshairs: {
        show: false,
      },
    },
    tooltip: {
      enabled: false,
    },
    legend: {
      show: false,
    },
    plotOptions: {
      area: {
        fillTo: "end",
      },
    },
  }), [color]);

  const series = useMemo(() => [
    {
      name: "Portfolio Size",
      data: chartData,
    },
  ], [chartData]);

  const chartHeight = useBreakpointValue({ base: 30, sm: 38, md: 45 }) || 38;

  return (
    <ReactApexChart
      options={chartOptions}
      series={series}
      type="area"
      height={chartHeight}
      width="100%"
    />
  );
};

export default PortfolioSizeChart;
